import {useEffect, useMemo, useState} from "react";
import {PlusIcon} from "@heroicons/react/24/outline";
import {useNotifications} from "../../../../../../../lib/NotificationContext.tsx";
import RelativeLoadingOverlayFrame from "../../../../../../../components/RelativeLoadingOverlayFrame.tsx";
import {createTable, RenderingPresetType} from "../../../../../../../components/table/CreateTable.tsx";
import {PrimaryButton} from "../../../../../../../components/general/PrimaryButton.tsx";
import Popup from "../../../../../../../components/general/Popup.tsx";
import CreateManualBookingModal
    from "../../../../../../../components/specific/CreateManualBookingModal/CreateManualBookingModal.tsx";
import {BookingRowActionsMenu} from "./BookingRowActionsMenu.tsx";
import type {GlAccount} from "../../../../../../../hooks/useGLAccounts.ts";
import type {BackendAPITypes} from "../../../../../../../generated/blueprint.ts";
import type {Booking} from "./GeneralJournalBookingsTab.model.ts";

interface GeneralJournalBookingsTabProps {
    businessId: string;
    glAccounts: GlAccount[];
    onFetchBookings: (businessId: string) => Promise<Booking[]>;
    onSaveBooking: (payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerMakeManualBookingPostRequestBody) => Promise<void>;
    onUpdateBooking: (payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerEditManualBookingPostRequestBody) => Promise<void>;
    onDeleteBooking: (payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerDeleteManualBookingPostRequestBody) => Promise<void>;
    onFetchBankAccounts: (businessId: string) => Promise<BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerEntrepreneurGetBankAccountsGetResponseBody>;
    onFetchBankBalance: (businessId: string, bankAccountId: string, date: string) => Promise<{
        balance: number | null;
        iban: string;
        institution_name: string
    }>;
}

export default function GeneralJournalBookingsTab({
    businessId,
    glAccounts,
                                                      onFetchBookings,
                                                      onSaveBooking,
                                                      onUpdateBooking,
                                                      onDeleteBooking,
                                                      onFetchBankAccounts,
                                                      onFetchBankBalance,
}: GeneralJournalBookingsTabProps) {
    const { dispatch } = useNotifications();
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewBookingId, setViewBookingId] = useState<string | null>(null);
    const [startInEdit, setStartInEdit] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [deleteBookingId, setDeleteBookingId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    async function loadBookings() {
        setIsLoading(true);
        try {
            const response = await onFetchBookings(businessId);
            setBookings(response);
        } catch {
            dispatch({
                title: "Kon boekingen niet laden",
                description: "De handmatige boekingen konden niet worden opgehaald.",
                type: "error",
            });
            setBookings([]);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadBookings();
    }, [businessId]);

    async function handleDeleteBooking() {
        if (!deleteBookingId || isDeleting) return;
        setIsDeleting(true);
        try {
            await onDeleteBooking({ client_business_id: businessId, booking_id: deleteBookingId });
            dispatch({ title: "Boeking verwijderd", description: "De handmatige boeking is succesvol verwijderd.", type: "success" });
            setDeleteBookingId(null);
            loadBookings();
        } catch {
            dispatch({ title: "Fout bij verwijderen", description: "De boeking kon niet worden verwijderd. Probeer het opnieuw.", type: "error" });
        } finally {
            setIsDeleting(false);
        }
    }

    // Create table component once — pass fresh data via the `data` prop
    const BookingsTable = useMemo(
        () =>
            createTable<Booking>([], {
                columns: [
                    {
                        name: "Datum",
                        field: "booking_date",
                        renderingPreset: RenderingPresetType.Date,
                        style: { widthMultiplier: 0.6 },
                    },
                    {
                        name: "Reden",
                        field: "audit_reason",
                        style: { widthMultiplier: 2 },
                    },
                    {
                        name: "Regels",
                        field: "lines",
                        customRender: (b: Booking) => <span>{b.lines.length}</span>,
                        style: { widthMultiplier: 0.4 },
                        disableSorting: true,
                    },
                    {
                        name: "",
                        field: "id",
                        style: { widthMultiplier: 0.3 },
                        disableSorting: true,
                        customRender: (b: Booking) => (
                            <div className="flex justify-end items-center">
                                <BookingRowActionsMenu
                                    onEdit={() => {
                                        setViewBookingId(b.id);
                                        setStartInEdit(true);
                                    }}
                                    onDelete={() => setDeleteBookingId(b.id)}
                                />
                            </div>
                        ),
                    },
                ],
                onEmptyRender: () => (
                    <div className="text-center py-12 text-gray-500 text-sm">
                        Geen handmatige boekingen gevonden voor deze klant.
                    </div>
                ),
                onRowClick: (b: Booking) => {
                    setViewBookingId(b.id);
                },
                options: {
                    allowFullRowClick: true,
                    disableBottomPaginationWidget: true,
                },
            }),
        [],
    );

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Memoriaal</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Beheer de handmatige boekingen voor deze klant.
                    </p>
                </div>
                <PrimaryButton
                    title="Nieuwe boeking"
                    icon={PlusIcon}
                    onClick={() => setIsCreateOpen(true)}
                />
            </div>

            <RelativeLoadingOverlayFrame isLoading={isLoading}>
                <BookingsTable data={bookings} />
            </RelativeLoadingOverlayFrame>

            <CreateManualBookingModal
                open={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                onSuccess={() => {
                    loadBookings();
                }}
                businessId={businessId}
                glAccounts={glAccounts}
                onSave={
                    (payload) => onSaveBooking(payload)
                }
                onFetchBankAccounts={async () => {
                    return await onFetchBankAccounts(businessId);
                }}
                onFetchBankBalance={async (bankAccountId: string, date: string) => {
                    return await onFetchBankBalance(businessId, bankAccountId, date);
                }}
            />

            <CreateManualBookingModal
                open={viewBookingId !== null}
                onClose={() => {
                    setViewBookingId(null);
                    setStartInEdit(false);
                }}
                onSuccess={() => {
                    loadBookings();
                }}
                businessId={businessId}
                booking={bookings.find(b => b.id === viewBookingId) ?? null}
                glAccounts={glAccounts}
                onUpdate={onUpdateBooking}
                startInEditMode={startInEdit}
                onFetchBankAccounts={async () => {
                    return await onFetchBankAccounts(businessId);
                }}
                onFetchBankBalance={async (bankAccountId: string, date: string) => {
                    return await onFetchBankBalance(businessId, bankAccountId, date);
                }}
            />

            <Popup
                open={deleteBookingId !== null}
                onClose={() => setDeleteBookingId(null)}
                onConfirm={handleDeleteBooking}
                title="Boeking verwijderen"
                variant="danger"
                confirmButtonDisplayLabel="Verwijderen"
                confirmButtonDisabled={isDeleting}
            >
                Weet je zeker dat je deze boeking wilt verwijderen?
            </Popup>
        </div>
    );
}
